import { capture } from '@afterpaytouch/integrations/monitoring'
import { ErrorCode, PortalApiError } from '@afterpaytouch/portal-api/types'
import { createCheckoutStartIdentityErrorHandler } from './identityErrorHandler'
import { createCheckoutStartRecoverableTerminalErrorHandler } from './recoverableTerminalErrorHandler'
import { createCheckoutStartAURegulationErrorHandler } from './auRegulationErrorHandler'
import { createCheckoutStartAffordabilityErrorHandler } from './affordabilityErrorHandler'
import { createCheckoutStartProfileErrorHandler } from './profileErrorHandler'
import { createCheckoutStartComplianceErrorHandler } from './complianceErrorHandler'
import { createCheckoutStartConvergenceIneligibleErrorHandler } from './convergenceIneligibleErrorHandler'
import { createCheckoutStartDefaultTerminalErrorHandler } from './defaultTerminalErrorHandler'
import { ErrorHandler, CheckoutErrorContext } from './types'
import { Route } from '../../config/router'

// Type for handler factory functions
type ErrorHandlerFactory = () => ErrorHandler

// Main Error Handler Factory
export const createCheckoutErrorHandler = (
  context: CheckoutErrorContext
): {
  handleError: (error: PortalApiError, errorCode: ErrorCode) => Promise<Route>
} => {
  const handlers: ErrorHandlerFactory[] = [
    createCheckoutStartConvergenceIneligibleErrorHandler,
    createCheckoutStartIdentityErrorHandler,
    createCheckoutStartRecoverableTerminalErrorHandler,
    createCheckoutStartAURegulationErrorHandler,
    createCheckoutStartAffordabilityErrorHandler,
    createCheckoutStartProfileErrorHandler,
    createCheckoutStartComplianceErrorHandler,
  ]

  const defaultHandler = createCheckoutStartDefaultTerminalErrorHandler()

  return {
    async handleError(error: PortalApiError, errorCode: ErrorCode): Promise<Route> {
      // Log the error
      if (error instanceof Error) {
        capture.error(error)
      }

      // Iterate through handlers until one can handle the error
      for (const handler of handlers) {
        const currentHandler = handler()
        if (currentHandler.canHandle(errorCode, error)) {
          return (await currentHandler.handle(errorCode, error, context)) as Route
        }
      }

      // Fallback to default handler (should never reach here since DefaultTerminalErrorHandler always returns true)
      return (await defaultHandler.handle(errorCode, error, context)) as Route
    },
  }
}

// Helper function for easy usage
export const createErrorHandler = (context: CheckoutErrorContext): ReturnType<typeof createCheckoutErrorHandler> => {
  return createCheckoutErrorHandler(context)
}

// Export types
export type { CheckoutErrorContext, ErrorHandler } from './types'

// Export individual handlers for testing
export {
  createCheckoutStartConvergenceIneligibleErrorHandler,
  createCheckoutStartIdentityErrorHandler,
  createCheckoutStartRecoverableTerminalErrorHandler,
  createCheckoutStartAURegulationErrorHandler,
  createCheckoutStartAffordabilityErrorHandler,
  createCheckoutStartProfileErrorHandler,
  createCheckoutStartComplianceErrorHandler,
  createCheckoutStartDefaultTerminalErrorHandler,
}
